import {useCallback, useLayoutEffect, useRef} from 'react'
import {getFocusableNodes, moveFocusInside} from 'focus-lock'

/**
 * # Focus hand-off between levels
 *
 * A level change hides the control that asked for it. The pane it sits in turns
 * `inert` for the swap, and on mobile that pane also slides away and ends up
 * `visibility: hidden`. The browser then drops focus, and the header focus lock
 * moves it to the first control it can still reach, which is outside the
 * navigation.
 *
 * This hook notes a hand-off when a level change starts, and pays it out only
 * when focus is really lost:
 *
 * - Focus kept: nothing moves. On desktop the level-1 list stays on screen, so
 *   the pressed control never loses focus. No breakpoint is read for this.
 * - Focus lost while the panes swap: park it on the tree root, which is inside
 *   the lock, and land it once the new level is on screen.
 * - Focus lost after the new level is on screen: land it at once. This is the
 *   mobile level-1 case, where the blur comes at the end of the slide.
 *
 * A drill-out lands on the control its drill-in was pressed on. A drill-in lands
 * on the first control of the pane, its breadcrumb, so the next Tab goes on into
 * the entries.
 *
 * ## Why the lock cannot do this
 *
 * `react-focus-lock` has no "focus was lost inside me, put it here" option. When
 * a focused control becomes unfocusable, it walks up that control's ancestors
 * and takes the first control it finds. With the panes blank, that walk leaves
 * the navigation. It ignores `data-autofocus` on this path.
 *
 * Retiming the panes so the walk stays inside the tree was measured. It does
 * place the drill-ins, but it leaves the outgoing pane in the tab order, loses
 * the drill-out origin, and breaks the desktop case.
 *
 * What the library can answer is left to it: `getFocusableNodes` says what the
 * browser will focus, `moveFocusInside` enters a pane. The lock calls these same
 * functions. Its React hooks (`useFocusController`) only wrap them in a sidecar
 * round-trip, and work only while a `FocusLock` is mounted.
 */

/** The panel owns its scroll position across a level change. */
const FOCUS_OPTIONS = {preventScroll: true}

/**
 * Whether the browser will still put focus on `element`.
 *
 * Reports rather than acts: focusing the element to find out would move focus
 * instead of answering about it. The answer comes from the lock's own list, so
 * the two can never disagree. The search is scoped to this element, but the
 * lock walks its ancestors to the root checking `display`, `visibility` and
 * `inert` — which is what makes an entry inside a closed pane answer false.
 *
 * Pass a specific control. `document.body` walks the whole document.
 *
 * @param {Element|null|undefined} element
 * @returns {boolean}
 */
export const isReachable = (element) =>
    Boolean(element?.isConnected) &&
    getFocusableNodes([element], new Map()).some(({node}) => node === element)

/**
 * Carries focus into the level a control just opened.
 *
 * @param {Object} options
 * @param {React.RefObject<HTMLElement>} options.rootRef - The tree root. It
 *   holds focus while a pane swaps, so it needs `tabIndex={-1}`, and it takes
 *   the `onFocusOut` handler, since every control is inside it.
 * @param {boolean} options.ready - Whether the level asked for is on screen with
 *   no pane inert. Focus is only paid out then.
 * @returns {{begin: Function, onFocusOut: Function}}
 *
 * @example
 * const {begin, onFocusOut} = useLevelFocus({
 *     rootRef,
 *     ready: !paneSwapping && presentedLevel === requestedLevel
 * })
 *
 * // Drilling in: land on the pane that opens, and keep the control pressed so
 * // the matching drill-out can go back to it.
 * const drillIn = (entry) => {
 *     originRef.current = begin(paneRef)
 *     setEntry(entry)
 * }
 *
 * // Drilling out: prefer that control, and fall back to the pane.
 * const drillOut = () => {
 *     begin(previousPaneRef, originRef.current)
 *     setEntry(null)
 * }
 *
 * // The root holds focus between panes, and hears every control below it.
 * return <div ref={rootRef} tabIndex={-1} onBlur={onFocusOut}>...</div>
 */
export const useLevelFocus = ({rootRef, ready}) => {
    // The hand-off in flight.
    const handoffRef = useRef(null)

    // Read in an event handler, outside the render that set it.
    const readyRef = useRef(ready)
    readyRef.current = ready

    const land = useCallback(() => {
        const handoff = handoffRef.current
        if (!handoff) return
        handoffRef.current = null
        // The control this level was opened from, while it is still there.
        if (isReachable(handoff.preferred)) {
            handoff.preferred.focus(FOCUS_OPTIONS)
            return
        }
        // Otherwise the pane, and the lock decides what inside it takes focus.
        moveFocusInside(handoff.pane.current, null, {focusOptions: FOCUS_OPTIONS})
    }, [])

    /**
     * Notes a hand-off for the level change about to be asked for.
     *
     * @param {React.RefObject<Element>} pane - The pane being opened, entered at
     *   its first reachable control.
     * @param {Element|null} [preferred] - Taken before the pane while it is
     *   still reachable. A drill-out names the control its drill-in was pressed
     *   on; a drill-in has nothing to go back to and names nothing.
     * @returns {Element|null} The control that asked for the change, so a
     *   drill-in can keep it for its own drill-out.
     */
    const begin = useCallback(
        (pane, preferred = null) => {
            const root = rootRef.current
            const origin = root?.ownerDocument.activeElement
            // Nothing in the tree is focused, so there is no focus to lose. A
            // pointer on a browser that does not focus a pressed button.
            //
            // The root itself is turned away too. It is focusable, and it holds
            // focus while a pane swaps, so a control pressed during that swap
            // would otherwise record the root as the control to go back to — and
            // the root never hides, so that hand-off could never be paid out and
            // focus would stay on it.
            if (!origin || origin === root || !root.contains(origin)) {
                handoffRef.current = null
                return null
            }
            handoffRef.current = {origin, pane, preferred, parked: false}
            return origin
        },
        [rootRef]
    )

    /**
     * Runs for every control in the tree, so it answers only for the one the
     * hand-off waits on.
     *
     * @param {React.FocusEvent} event
     */
    const onFocusOut = useCallback(
        (event) => {
            const handoff = handoffRef.current
            if (!handoff || event.target !== handoff.origin) return
            // Focus went somewhere on purpose, so the shopper has answered it.
            if (event.relatedTarget) {
                handoffRef.current = null
                return
            }
            const root = rootRef.current
            // A blur from a control the shopper can still reach is not a pane
            // taking it away. A press on page chrome, or the window going to
            // another app, looks the same. The note stays for the change that
            // does hide it, which may not be this one.
            if (isReachable(handoff.origin)) return
            if (readyRef.current) {
                land()
                return
            }
            // Parked here, not left on `body`: the root is inside the lock, so
            // the lock has no reason to move focus while the panes swap.
            handoff.parked = true
            root.focus(FOCUS_OPTIONS)
        },
        [land, rootRef]
    )

    useLayoutEffect(() => {
        const handoff = handoffRef.current
        // A note nobody claimed stays past its level change on purpose: on
        // mobile the level-1 pane hides its control at the end of a transition,
        // long after this runs. The blur is filtered, not the waiting.
        if (!ready || !handoff?.parked) return
        const root = rootRef.current
        // Only pays out focus this hook holds. Anywhere else, the shopper has
        // taken it back and it is theirs.
        if (root && root.ownerDocument.activeElement === root) land()
        else handoffRef.current = null
    }, [ready, land, rootRef])

    return {begin, onFocusOut}
}
